/**
 * Recording blob cache (ACA-79)
 *
 * The web client records a consult's audio in the browser; while its upload is
 * in flight the audio must remain available so a failed upload can be retried.
 *
 * This is a synchronous in-memory layer (so the History Retry button can read a
 * blob without awaiting) backed by IndexedDB via {@link recordingBlobStore} for
 * durability across a full page reload/reopen. On startup call
 * {@link rehydrateRecordingBlobs} to repopulate memory from IndexedDB, then
 * {@link resumePendingUploads} to re-drive any uploads that never completed.
 */
import {
    putRecording,
    getAllRecordings,
    deleteRecording,
} from './recordingBlobStore'

interface CachedRecording {
    audioId: string | null
    blob: Blob
}

const cache = new Map<string, CachedRecording>()

/**
 * Remember a consult's full recording so a failed upload can be retried.
 * Writes through to IndexedDB (best-effort, fire-and-forget) for durability.
 */
export function setRecordingBlob(
    consultId: string,
    audioId: string | null,
    blob: Blob | null
): void {
    if (!consultId || !blob || blob.size === 0) return
    cache.set(consultId, { audioId, blob })
    void putRecording({ consultId, audioId, blob, createdAt: Date.now() })
}

/** Retrieve a consult's cached recording, if available. */
export function getRecordingBlob(consultId: string): Blob | undefined {
    return cache.get(consultId)?.blob
}

/** Retrieve the audioId associated with a cached recording, if available. */
export function getRecordingAudioId(
    consultId: string
): string | null | undefined {
    return cache.get(consultId)?.audioId
}

/** Drop a consult's cached recording from memory and IndexedDB. */
export function clearRecordingBlob(consultId: string): void {
    cache.delete(consultId)
    void deleteRecording(consultId)
}

/**
 * Repopulate the in-memory cache from IndexedDB (call once on startup).
 * In-memory entries win over persisted ones. Returns the rehydrated records.
 */
export async function rehydrateRecordingBlobs(): Promise<
    { consultId: string; audioId: string | null; blob: Blob }[]
> {
    const records = await getAllRecordings()
    for (const r of records) {
        // Only setRecordingBlob writes to the store, and it rejects empty blobs,
        // so a persisted record always has real audio. (Don't gate on blob.size
        // here: some structured-clone paths report 0 for a round-tripped Blob.)
        if (!cache.has(r.consultId) && r.blob) {
            cache.set(r.consultId, { audioId: r.audioId, blob: r.blob })
        }
    }
    return records.map((r) => ({
        consultId: r.consultId,
        audioId: r.audioId,
        blob: r.blob,
    }))
}
